/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — HUD
 * ===========================================================================
 *
 * Everything the player reads while fighting, built to the reference plate's
 * section F: health + portrait bottom-left, musou under it, KO counter top
 * right, minimap in a corner, officer bar top-centre, mission log down the
 * left edge, floating damage numbers in world space, big centre banners, and
 * a front end (title / pause / options / results) around it.
 *
 * ---------------------------------------------------------------------------
 * HOW IT IS BUILT, AND WHY
 * ---------------------------------------------------------------------------
 * DOM+CSS for anything that is a rectangle, a gradient or a piece of small
 * text: it is resolution-independent, composited by the browser, and razor
 * sharp at 4K for free. CANVAS for the four things CSS does badly — the big
 * numerals (they must not read as system text), the portrait plate, the crest
 * and the minimap.
 *
 * ANIMATION IS DRIVEN FROM JS, NOT CSS. Playwright screenshots the page with
 * `animations:'disabled'`, which cancels infinite CSS animations back to their
 * initial state — a CSS-keyframed musou glow would be invisible in every
 * critic frame and in every capture. So every pulse, punch, slide and fade
 * below is integrated from `world.time.real` in lateUpdate. It also means the
 * HUD's motion is deterministic under `__DW.step()`.
 *
 * WHY lateUpdate. Damage numbers are projected through `world.camera`, and the
 * camera rig solves its pose in ITS lateUpdate (ORDER.CAMERA = 90). Running
 * the HUD's presentation at ORDER.UI = 100 in the same pass means numbers,
 * reticle and minimap are consistent with the frame that is about to be
 * rendered rather than one frame stale. `update()` is used only for the input
 * pass (look sensitivity), which must happen before the camera consumes it.
 *
 * ALLOCATION. The damage layer is a fixed pool with a pre-built string table
 * (see damage.js). DOM writes are all guarded by a last-value cache, so a
 * still frame writes nothing at all; only values that actually moved touch the
 * DOM. Nothing here allocates a Vector3, array or object per frame.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API  (world.ui)
 * ---------------------------------------------------------------------------
 *   root                       the HUD element (contract name, kept)
 *   toast(text, opts)          log line, promoted to a banner when it shouts
 *   damage(x,y,z, amount,kind) world-space floating number ('hit' | 'kill' |
 *                              'crit' | 'block' | 'player'; a string amount is
 *                              drawn verbatim, e.g. 'GUARD')
 *   banner(text, sub, secs)    big centre event banner
 *   log(text, kind)            mission log line ('' | 'good' | 'warn' | 'bad')
 *   setState(name)             'title' | 'playing' | 'pause' | 'options' |
 *                              'results'
 *   showResults(data)          results screen (fields default from the world)
 *   minimap                    { canvas, draw, build, toMap, built }
 *   hide() / show()            drop or restore the whole overlay
 *   settings                   live options model
 *   setQuality(t) setVolume(bus,v)
 */
import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { CFG, QUALITY, applyQuality } from '../core/config.js';
import { clamp, clamp01, damp } from '../core/mathx.js';
import { CSS } from './style.js';
import { fit, numeral, portrait } from './gfx.js';
import { createDamageLayer } from './damage.js';
import { createMinimap } from './minimap.js';
// The HUD shows the Battle Art threshold, so it reads the price from the one
// table that owns it rather than hard-coding a 3 that would drift.
import { BRAVERY } from '../combat/moveset.js';
import { factionOf, SIDE } from '../core/theme.js';
import { ARENA_NAME } from '../world/layout.js';
import { createMenus, BUS_MAX } from './menus.js';
import { SELECT_CSS } from './select.js';

const LOG_LINES = 7;
/* The plate the HUD paints bottom-left. Warrior select writes this through
 * `ui.setWarrior()`, so the man on the health bar is the man the player
 * picked rather than a fixed illustration of the default hero. */
let heroArt = {};
const _v3 = new THREE.Vector3();
const DEF_NAME = 'Arya Bhumi';
const DEF_TITLE = 'Wilwatikta Vanguard';

/* The two hosts, named once in theme.js. The morale bar used to spell them
 * out in markup, which is how the labels kept their old names through a
 * faction rename that had already reached every other surface. */
const ALLIES = factionOf(SIDE.PLAYER), FOES = factionOf(SIDE.ENEMY);

/* Responsive overrides live in the HUD's existing injected stylesheet so the
 * interface still installs and disposes as one unit.  Desktop remains the
 * 1600 x 900 reference composition; narrow screens get a deliberately
 * reflowed HUD instead of shrinking that composition below legible size. */
const RESPONSIVE_CSS = `
.sb{
  --safe-t:env(safe-area-inset-top,0px);
  --safe-r:env(safe-area-inset-right,0px);
  --safe-b:env(safe-area-inset-bottom,0px);
  --safe-l:env(safe-area-inset-left,0px)
}
.sb .keys-compact,.sb .keys-touch,.sb .hint-touch{display:none}
.sb .touch-controls{display:none;position:absolute;inset:0;z-index:5;pointer-events:none;text-shadow:none}
.sb.touch-capable.ui-playing .touch-controls{display:block}
.sb .touch-stick{
  position:absolute;left:calc(var(--safe-l) + 12px);bottom:calc(var(--safe-b) + 12px);
  width:112px;height:112px;border-radius:50%;pointer-events:auto;touch-action:none;
  background:radial-gradient(circle,rgba(245,220,170,.08) 0 42%,rgba(8,7,6,.44) 44% 100%);
  border:1px solid rgba(238,202,136,.34);box-shadow:inset 0 0 22px rgba(0,0,0,.48),0 3px 16px rgba(0,0,0,.42)
}
.sb .touch-stick::before,.sb .touch-stick::after{content:"";position:absolute;background:rgba(240,210,150,.16)}
.sb .touch-stick::before{left:15px;right:15px;top:55px;height:1px}
.sb .touch-stick::after{top:15px;bottom:15px;left:55px;width:1px}
.sb .touch-stick-knob{
  position:absolute;left:50%;top:50%;width:46px;height:46px;margin:-23px 0 0 -23px;border-radius:50%;
  background:radial-gradient(circle at 38% 34%,rgba(255,242,205,.42),rgba(179,126,52,.28) 48%,rgba(7,6,5,.78) 100%);
  border:1px solid rgba(255,224,164,.6);box-shadow:0 3px 12px rgba(0,0,0,.6);will-change:transform
}
.sb .touch-actions{
  position:absolute;right:calc(var(--safe-r) + 12px);bottom:calc(var(--safe-b) + 12px);
  width:112px;height:112px;display:grid;grid-template-columns:repeat(2,52px);grid-template-rows:repeat(2,52px);
  gap:8px;pointer-events:none
}
.sb .touch-action,.sb .touch-pause{
  pointer-events:auto;touch-action:none;user-select:none;-webkit-user-select:none;border:1px solid rgba(244,207,137,.48);
  color:#fff0cf;background:radial-gradient(circle at 38% 30%,rgba(246,216,154,.22),rgba(12,9,7,.76) 72%);
  font:700 9px/1 "Liberation Sans","Helvetica Neue",Arial,sans-serif;letter-spacing:1.2px;text-transform:uppercase;
  box-shadow:inset 0 0 0 1px rgba(0,0,0,.55),0 3px 14px rgba(0,0,0,.5)
}
.sb .touch-action{width:52px;height:52px;border-radius:50%}
.sb .touch-action.held,.sb .touch-pause:active{color:#1a1006;background:#f0bf65;transform:scale(.94)}
.sb .touch-pause{
  position:absolute;right:calc(var(--safe-r) + 14px);top:calc(var(--safe-t) + 122px);
  width:46px;height:46px;clip-path:polygon(8px 0,100% 0,calc(100% - 8px) 100%,0 100%);
  font-size:12px;letter-spacing:2px
}
.sb.touch-capable.ui-playing .plate{bottom:calc(var(--safe-b) + 136px)}
.sb.touch-capable.ui-playing .map{bottom:calc(var(--safe-b) + 136px)}
.sb.touch-capable .btn{min-height:44px;font-size:max(calc(var(--u)*12.5),11px);letter-spacing:max(calc(var(--u)*5.5),2px)}
.sb.touch-capable .seg{min-width:44px;min-height:44px;padding:8px 6px}
.sb.touch-capable .slide{min-height:44px}
.sb.touch-capable .opts .body{
  max-height:calc(100% - var(--safe-t) - var(--safe-b) - 12px);overflow-y:auto;overscroll-behavior:contain
}
.sb.touch-capable .keys-wide,.sb.touch-capable .keys-compact,.sb.touch-capable .hint-desktop{display:none}
.sb.touch-capable .keys-touch,.sb.touch-capable .hint-touch{display:inline}

@media (max-width:600px){
  .sb .plate{left:max(calc(var(--u)*18),calc(var(--safe-l) + 10px));bottom:calc(var(--safe-b) + var(--u)*24);gap:calc(var(--u)*8)}
  .sb .pf{width:calc(var(--u)*64);height:calc(var(--u)*64)}
  .sb .pcol{width:calc(var(--u)*205)}
  .sb .ptitle{display:none}
  .sb .pname{font-size:calc(var(--u)*17);letter-spacing:calc(var(--u)*1.8)}
  .sb .hpnum,.sb .mulab{width:calc(var(--u)*54)}
  .sb .map{right:max(calc(var(--u)*18),calc(var(--safe-r) + 10px));bottom:calc(var(--safe-b) + var(--u)*24)}
  .sb .off{top:calc(var(--safe-t) + 118px);width:calc(100% - var(--safe-l) - var(--safe-r) - 34px);margin-left:0;transform:translateX(-50%)}
  .sb .ko{right:max(calc(var(--u)*18),calc(var(--safe-r) + 10px));top:calc(var(--safe-t) + var(--u)*18)}
  .sb .ko .num{width:calc(var(--u)*150);height:calc(var(--u)*56)}
  .sb .ko .rule{width:calc(var(--u)*86);margin-top:calc(var(--u)*2)}
  .sb .ko .clock{margin-top:calc(var(--u)*6)}
  .sb .log{left:max(calc(var(--u)*18),calc(var(--safe-l) + 10px));top:calc(var(--safe-t) + 158px);width:calc(var(--u)*270)}
  .sb .log .line:nth-child(n+4){display:none}
  .sb .chain{right:max(calc(var(--u)*12),calc(var(--safe-r) + 6px));top:46%}
  .sb .chain .num{width:calc(var(--u)*150);height:calc(var(--u)*60)}
  .sb .ban{top:34%}
  .sb .ban .bt{font-size:calc(var(--u)*28);letter-spacing:calc(var(--u)*4);white-space:nowrap;overflow:hidden;padding-left:12px;padding-right:12px}
  .sb .ban .bs{font-size:calc(var(--u)*9);letter-spacing:calc(var(--u)*4)}
  .sb .modal{padding:var(--safe-t) var(--safe-r) var(--safe-b) var(--safe-l)}
  .sb .modal .body{width:100%;max-width:100%;padding-left:12px;padding-right:12px}
  .sb .title .body{margin-top:0}
  .sb .title .mtitle{font-size:calc(var(--u)*42);letter-spacing:calc(var(--u)*8);line-height:1.08}
  .sb .title .crest{width:calc(var(--u)*104);height:calc(var(--u)*104);margin-bottom:calc(var(--u)*7)}
  .sb .mrule{width:min(calc(var(--u)*440),calc(100% - 24px))}
  .sb .keys{bottom:calc(var(--safe-b) + 10px);padding:0 max(12px,var(--safe-l)) 0 max(12px,var(--safe-r));font-size:9px;letter-spacing:1.5px;line-height:1.45}
  .sb .keys-wide{display:none}
  .sb .keys-compact{display:inline}
  .sb.touch-capable .keys-compact{display:none}
  .sb .hint{margin-top:calc(var(--u)*18);font-size:9px;letter-spacing:2.2px}
  .sb .res>.body{width:100%}
  .sb .res .rtitle{font-size:calc(var(--u)*42)!important;letter-spacing:calc(var(--u)*7)!important}
  .sb .res .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;width:100%;margin-top:calc(var(--u)*14)}
  .sb .res .cell{min-width:0}
  .sb .res .rank{margin-top:calc(var(--u)*14)}
  .sb .res .rank .rk{font-size:calc(var(--u)*72)}
  .sb .rows{width:100%;max-width:calc(var(--u)*490)}
  .sb .row{gap:calc(var(--u)*8)}
  .sb .row>.k{flex-basis:calc(var(--u)*158);letter-spacing:calc(var(--u)*2.4)}
  .sb#hud .mcap{bottom:calc(var(--safe-b) + 175px);width:calc(var(--u)*260);margin-left:calc(var(--u)*-130)}
  .sb#hud.touch-capable.ui-playing .mcap{bottom:calc(var(--safe-b) + 210px)}
  .sb.touch-capable.ui-playing .map{top:calc(var(--safe-t) + 174px);bottom:auto;width:112px;height:112px}
}

@media (max-height:500px){
  .sb .keys{bottom:calc(var(--safe-b) + 8px);font-size:9px;letter-spacing:1.5px}
  .sb.touch-capable.ui-playing .plate{left:calc(var(--safe-l) + 136px);bottom:calc(var(--safe-b) + 18px)}
  .sb.touch-capable.ui-playing .map{right:calc(var(--safe-r) + 148px);bottom:calc(var(--safe-b) + 16px);width:112px;height:112px}
  .sb.touch-capable .touch-pause{top:calc(var(--safe-t) + 8px);right:calc(var(--safe-r) + 190px)}
  .sb#hud.touch-capable.ui-playing .mcap{bottom:calc(var(--safe-b) + 132px)}
}
`;

/* --------------------------------------------------------------- helpers -- */

/** Cached style writes: a still HUD writes nothing to the DOM. */
function sx(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__sx !== undefined && Math.abs(el.__sx - v) < 0.0012) return;
  el.__sx = v;
  el.style.transform = 'scaleX(' + v.toFixed(4) + ')';
}
function op(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__op !== undefined && Math.abs(el.__op - v) < 0.004) return;
  el.__op = v;
  el.style.opacity = v.toFixed(3);
}
function tx(el, s) { if (el.__tx === s) return; el.__tx = s; el.textContent = s; }
function px(el, prop, v) {
  const k = '__p' + prop;
  if (el[k] !== undefined && Math.abs(el[k] - v) < 0.4) return;
  el[k] = v;
  el.style[prop] = v.toFixed(1) + 'px';
}
function cls(el, name, on) {
  const k = '__c' + name;
  if (el[k] === on) return;
  el[k] = on;
  el.classList.toggle(name, on);
}
const mmss = (s) => {
  s = Math.max(0, s | 0);
  const m = (s / 60) | 0, r = s % 60;
  return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
};

/* ========================================================================== */

export function createHUD(root) {
  let W = null, el = null, engine = null;
  let u = 1, dpr = 1, fxDpr = 1, vw = 1600, vh = 900;
  let hidden = false, allowEngineControl = false;

  const dmg = createDamageLayer();
  const mm = createMinimap();
  let menus = null;
  let touchCleanup = null;
  let touchReleaseAll = null;

  /* ------------------------------------------------------------ settings */
  const settings = {
    quality: CFG.quality,
    master: 0.72, music: 0.40, sfx: 0.72,
    sensitivity: 1.0,
    vignette: true, damage: true,
  };

  function save() {
    if (CFG.debug.headless) return;
    try { localStorage.setItem('sb.settings', JSON.stringify(settings)); } catch (e) { /* private mode */ }
  }
  function load() {
    if (CFG.debug.headless) return;
    try {
      const s = JSON.parse(localStorage.getItem('sb.settings') || 'null');
      if (s && typeof s === 'object') for (const k in settings) if (k in s && typeof s[k] === typeof settings[k]) settings[k] = s[k];
    } catch (e) { /* ignore */ }
  }

  function setVolume(bus, v) {
    v = clamp01(v);
    if (bus in settings) settings[bus] = v;
    const max = BUS_MAX[bus] !== undefined ? BUS_MAX[bus] : 1;
    try { W && W.audio && W.audio.setBus && W.audio.setBus(bus, v * max); } catch (e) { /* ignore */ }
    save();
    return v;
  }
  function setQuality(name) {
    if (!QUALITY[name]) return settings.quality;
    settings.quality = name;
    applyQuality(name);
    try { W && W.bus && W.bus.emit('quality', name); } catch (e) { /* ignore */ }
    try { engine && engine.resize && engine.resize(); } catch (e) { /* ignore */ }
    save();
    return name;
  }
  const sound = (id) => { try { W && W.audio && W.audio.play && W.audio.play(id); } catch (e) { /* ignore */ } };

  /* ---------------------------------------------------------------- refs */
  const R = {};
  let fxCtx = null;

  /* ------------------------------------------------------- runtime state */
  const S = {
    hp: 1, hpGhost: 1, hpHold: 0, hpPrev: 1,
    musou: 0, musouGlow: 0, brvCap: -1, brvN: -1,
    kills: -1, koPunch: 99,
    chain: -1, chainShow: 0, chainPunch: 99,
    off: 0, offHp: 1, offGhost: 1, offHold: 0, offId: -1, offVuln: 0,
    hurt: 0, low: 0, flash: 0,
    clock: '', statT: 99, mapT: 0,
    banner: { txt: '', sub: '', t: 99, dur: 0 },
    tide: 0.5,
  };
  const logs = [];
  // reticle dirty rect from the previous frame
  let rt0x = 0, rt0y = 0, rt1x = 0, rt1y = 0, rtDirty = false;

  /* ============================================================== markup */

  function build() {
    el = document.createElement('div');
    el.className = 'sb';
    el.id = 'hud';
    const style = document.createElement('style');
    style.textContent = CSS + SELECT_CSS + RESPONSIVE_CSS;
    el.appendChild(style);

    const main = document.createElement('div');
    main.className = 'hudlayer';
    main.innerHTML = `
      <div class="vig"></div>
      <div class="corners"></div>
      <div class="vig-low"></div>
      <div class="vig-hurt"></div>
      <canvas class="fxc"></canvas>
      <div class="flashwipe"></div>

      <div class="plate">
        <div class="pf"><canvas></canvas></div>
        <div class="pcol">
          <div class="prow"><span class="pname ser"></span><span class="ptitle"></span></div>
          <div class="barwrap">
            <div class="bar hp"><i class="ghost"></i><i class="fill"></i><b class="segs"></b>
              <b class="gloss"></b><b class="edge"></b></div>
            <div class="hpnum"><span></span><s></s></div>
          </div>
          <div class="barwrap">
            <div class="bar mu"><i class="fill"></i><i class="rdy"></i><b class="segs"></b>
              <b class="gloss"></b><b class="edge"></b></div>
            <div class="mulab">Musou</div>
          </div>
          <div class="brv"><span class="pips"></span><div class="brvlab">Bravery</div></div>
        </div>
      </div>

      <div class="ko">
        <canvas class="num"></canvas>
        <div class="rule"></div>
        <div class="kolab">K.O.</div>
        <div class="clock"><em>Field</em><span>00:00</span></div>
      </div>

      <div class="chain">
        <canvas class="num"></canvas>
        <div class="chlab">Hits</div>
        <div class="best"></div>
      </div>

      <div class="off">
        <div class="vuln">Opening</div>
        <div class="plaque">
          <div class="otop"><span class="oname ser"></span></div>
          <div class="obar"><i class="ghost"></i><i class="fill"></i>
            <b class="notch" style="left:50%"></b><b class="notch" style="left:20%"></b></div>
          <div class="obot"><span class="otitle"></span>
            <span class="pips"><i></i><i></i><i></i></span>
            <span class="odist"></span></div>
        </div>
      </div>

      <div class="map">
        <div class="tlab"><b>${ALLIES.short}</b><u>${FOES.short}</u></div>
        <div class="tide"><i></i><u></u></div>
        <canvas></canvas>
        <div class="mtag">${ARENA_NAME}</div>
      </div>

      <div class="log"></div>

      <div class="ban"><div class="rule"></div><div class="bt ser"></div><div class="bs"></div><div class="rule r2"></div></div>

      <div class="stat"></div>

      <div class="touch-controls" aria-label="Touch battle controls">
        <button type="button" class="touch-pause" data-touch-menu="pause" aria-label="Pause battle">Ⅱ</button>
        <div class="touch-stick" role="application" aria-label="Movement stick">
          <i class="touch-stick-knob"></i>
        </div>
        <div class="touch-actions" aria-label="Battle actions">
          <button type="button" class="touch-action" data-touch-key="KeyK" aria-label="Charge attack">Charge</button>
          <button type="button" class="touch-action" data-touch-key="KeyL" aria-label="Musou attack">Musou</button>
          <button type="button" class="touch-action" data-touch-key="Space" aria-label="Dodge">Dodge</button>
          <button type="button" class="touch-action" data-touch-key="KeyJ" aria-label="Light attack">Attack</button>
        </div>
      </div>`;
    el.appendChild(main);

    R.vigLow = main.querySelector('.vig-low');
    R.vigHurt = main.querySelector('.vig-hurt');
    R.vig = main.querySelector('.vig');
    R.flash = main.querySelector('.flashwipe');
    R.fx = main.querySelector('.fxc');
    R.portrait = main.querySelector('.pf canvas');
    R.pname = main.querySelector('.pname');
    R.ptitle = main.querySelector('.ptitle');
    R.hpGhost = main.querySelector('.hp .ghost');
    R.hpFill = main.querySelector('.hp .fill');
    R.hpNum = main.querySelector('.hpnum span');
    R.hpMax = main.querySelector('.hpnum s');
    R.muFill = main.querySelector('.mu .fill');
    R.muRdy = main.querySelector('.mu .rdy');
    R.muLab = main.querySelector('.mulab');
    R.brvPips = main.querySelector('.brv .pips');
    R.brvLab = main.querySelector('.brvlab');
    R.ko = main.querySelector('.ko');
    R.koNum = main.querySelector('.ko .num');
    R.clock = main.querySelector('.clock span');
    R.chain = main.querySelector('.chain');
    R.chainNum = main.querySelector('.chain .num');
    R.chainBest = main.querySelector('.chain .best');
    R.off = main.querySelector('.off');
    R.offName = main.querySelector('.oname');
    R.offTitle = main.querySelector('.otitle');
    R.offDist = main.querySelector('.odist');
    R.offGhost = main.querySelector('.obar .ghost');
    R.offFill = main.querySelector('.obar .fill');
    R.offVuln = main.querySelector('.off .vuln');
    R.pips = main.querySelectorAll('.pips i');
    R.map = main.querySelector('.map canvas');
    R.tideA = main.querySelector('.tide i');
    R.tideB = main.querySelector('.tide u');
    R.log = main.querySelector('.log');
    R.ban = main.querySelector('.ban');
    R.banT = main.querySelector('.ban .bt');
    R.banS = main.querySelector('.ban .bs');
    R.banR = main.querySelectorAll('.ban .rule');
    R.stat = main.querySelector('.stat');

    for (let i = 0; i < LOG_LINES; i++) {
      const d = document.createElement('div');
      d.className = 'line';
      d.innerHTML = '<b></b><span></span>';
      R.log.appendChild(d);
      logs.push({ el: d, span: d.querySelector('span'), t: 99, life: 0, kind: '' });
    }

    R.fx.style.pointerEvents = 'none';
    root.appendChild(el);
  }

  /* ========================================================= touch input */

  function installTouch(w) {
    const capable = (navigator.maxTouchPoints || 0) > 0 ||
      !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches);
    cls(el, 'touch-capable', capable);
    cls(el, 'ui-playing', !menus || menus.state === 'playing');
    if (!capable) return;

    const stick = el.querySelector('.touch-stick');
    const knob = el.querySelector('.touch-stick-knob');
    const actions = el.querySelectorAll('[data-touch-key]');
    const pause = el.querySelector('[data-touch-menu="pause"]');
    const canvas = document.getElementById('gl');
    const held = { KeyW: false, KeyA: false, KeyS: false, KeyD: false,
      KeyJ: false, KeyK: false, KeyL: false, Space: false };
    let stickId = -1;
    let lookId = -1, lookX = 0, lookY = 0;

    const key = (code, down) => {
      if (!(code in held) || held[code] === down) return;
      held[code] = down;
      window.dispatchEvent(new KeyboardEvent(down ? 'keydown' : 'keyup', {
        code, key: code === 'Space' ? ' ' : '', bubbles: true, cancelable: true,
      }));
    };
    const centreStick = () => { if (knob) knob.style.transform = 'translate(0px,0px)'; };
    const releaseAll = () => {
      for (const code in held) key(code, false);
      stickId = -1; lookId = -1;
      centreStick();
      for (let i = 0; i < actions.length; i++) {
        actions[i].__touchPointer = -1;
        actions[i].classList.remove('held');
      }
    };
    touchReleaseAll = releaseAll;

    const steer = (ev) => {
      const r = stick.getBoundingClientRect();
      let x = ev.clientX - (r.left + r.width * 0.5);
      let y = ev.clientY - (r.top + r.height * 0.5);
      const len = Math.hypot(x, y);
      const radius = Math.max(18, Math.min(r.width, r.height) * 0.31);
      if (len > radius) { x *= radius / len; y *= radius / len; }
      knob.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
      const gate = radius * 0.28;
      key('KeyA', x < -gate); key('KeyD', x > gate);
      key('KeyW', y < -gate); key('KeyS', y > gate);
    };
    const stickDown = (ev) => {
      if (stickId !== -1) return;
      ev.preventDefault(); ev.stopPropagation();
      stickId = ev.pointerId;
      try { stick.setPointerCapture(ev.pointerId); } catch (e) { /* capture unsupported */ }
      steer(ev);
    };
    const stickMove = (ev) => { if (ev.pointerId === stickId) { ev.preventDefault(); steer(ev); } };
    const stickUp = (ev) => {
      if (ev.pointerId !== stickId) return;
      ev.preventDefault(); stickId = -1;
      key('KeyW', false); key('KeyA', false); key('KeyS', false); key('KeyD', false);
      centreStick();
    };
    stick.addEventListener('pointerdown', stickDown, { passive: false });
    stick.addEventListener('pointermove', stickMove, { passive: false });
    stick.addEventListener('pointerup', stickUp, { passive: false });
    stick.addEventListener('pointercancel', stickUp, { passive: false });
    stick.addEventListener('lostpointercapture', stickUp, { passive: false });

    for (let i = 0; i < actions.length; i++) {
      const b = actions[i];
      b.__touchPointer = -1;
      b.addEventListener('pointerdown', (ev) => {
        if (b.__touchPointer !== -1) return;
        ev.preventDefault(); ev.stopPropagation();
        b.__touchPointer = ev.pointerId;
        try { b.setPointerCapture(ev.pointerId); } catch (e) { /* capture unsupported */ }
        b.classList.add('held'); key(b.dataset.touchKey, true);
      }, { passive: false });
      const up = (ev) => {
        if (ev.pointerId !== b.__touchPointer) return;
        ev.preventDefault(); b.__touchPointer = -1;
        b.classList.remove('held'); key(b.dataset.touchKey, false);
      };
      b.addEventListener('pointerup', up, { passive: false });
      b.addEventListener('pointercancel', up, { passive: false });
      b.addEventListener('lostpointercapture', up, { passive: false });
    }

    pause.addEventListener('pointerdown', (ev) => {
      ev.preventDefault(); ev.stopPropagation();
      releaseAll(); sound('ui_back'); menus && menus.show('pause');
    }, { passive: false });

    const lookDown = (ev) => {
      if (ev.pointerType !== 'touch' || lookId !== -1 || !menus || menus.state !== 'playing') return;
      ev.preventDefault(); lookId = ev.pointerId; lookX = ev.clientX; lookY = ev.clientY;
      try { canvas.setPointerCapture(ev.pointerId); } catch (e) { /* capture unsupported */ }
    };
    const lookMove = (ev) => {
      if (ev.pointerId !== lookId) return;
      ev.preventDefault();
      const dx = ev.clientX - lookX, dy = ev.clientY - lookY;
      lookX = ev.clientX; lookY = ev.clientY;
      if (w.input && w.input._lookRaw) {
        w.input._lookRaw.x += dx * 0.72;
        w.input._lookRaw.y += dy * 0.72;
      }
    };
    const lookUp = (ev) => { if (ev.pointerId === lookId) { ev.preventDefault(); lookId = -1; } };
    if (canvas) {
      canvas.addEventListener('pointerdown', lookDown, { passive: false });
      canvas.addEventListener('pointermove', lookMove, { passive: false });
      canvas.addEventListener('pointerup', lookUp, { passive: false });
      canvas.addEventListener('pointercancel', lookUp, { passive: false });
      canvas.addEventListener('lostpointercapture', lookUp, { passive: false });
    }
    const visibility = () => { if (document.hidden) releaseAll(); };
    window.addEventListener('blur', releaseAll);
    document.addEventListener('visibilitychange', visibility);

    touchCleanup = () => {
      releaseAll();
      window.removeEventListener('blur', releaseAll);
      document.removeEventListener('visibilitychange', visibility);
      if (canvas) {
        canvas.removeEventListener('pointerdown', lookDown);
        canvas.removeEventListener('pointermove', lookMove);
        canvas.removeEventListener('pointerup', lookUp);
        canvas.removeEventListener('pointercancel', lookUp);
        canvas.removeEventListener('lostpointercapture', lookUp);
      }
      touchReleaseAll = null;
    };
  }

  /* ============================================================== layout */

  function layout(w, h, d) {
    if (!el) return;
    vw = Math.max(2, w | 0); vh = Math.max(2, h | 0);
    dpr = clamp(d || window.devicePixelRatio || 1, 0.75, 2.5);
    u = clamp(Math.min(vw / 1600, vh / 900), 0.72, 2.4);
    el.style.setProperty('--u', u.toFixed(4) + 'px');

    // fx layer: capped backing store so 4K costs what 1080p costs
    fxDpr = Math.min(dpr, 1.5);
    const bw = Math.max(2, Math.round(vw * fxDpr)), bh = Math.max(2, Math.round(vh * fxDpr));
    if (R.fx.width !== bw || R.fx.height !== bh) { R.fx.width = bw; R.fx.height = bh; }
    fxCtx = R.fx.getContext('2d');
    fxCtx.setTransform(fxDpr, 0, 0, fxDpr, 0, 0);
    fxCtx.clearRect(0, 0, vw, vh);
    dmg.clear();
    rtDirty = false;

    const pd = Math.min(dpr, 2);
    const ps = 78 * u;
    portrait(fit(R.portrait, ps, ps, pd), ps, ps, heroArt);

    S.kills = -1; S.chain = -1;      // force a numeral redraw at the new size
    drawKO(0);
    drawChain(0);
    if (menus) menus.resize();
  }

  function drawKO(v) {
    const w = 236 * u, h = 88 * u;
    const c = fit(R.koNum, w, h, Math.min(dpr, 2));
    numeral(c, String(v | 0), w, h, { size: h * 0.90, align: 'right', ramp: 'gold' });
  }
  function drawChain(v) {
    const w = 230 * u, h = 92 * u;
    const c = fit(R.chainNum, w, h, Math.min(dpr, 2));
    numeral(c, String(v | 0), w, h, {
      size: h * 0.92, align: 'right', ramp: 'ember',
      glow: 'rgba(255,140,40,.75)', glowR: 0.30, key: 'rgba(180,70,10,.9)',
    });
  }

  /* ============================================================ messaging */

  function log(text, kind) {
    if (!el || text == null) return;
    // recycle the oldest slot and move it to the top of the stack
    let oldest = logs[0];
    for (let i = 1; i < logs.length; i++) if (logs[i].t > oldest.t) oldest = logs[i];
    oldest.t = 0;
    oldest.life = 7.2;
    tx(oldest.span, String(text));
    if (oldest.kind !== kind) {
      oldest.el.className = 'line' + (kind ? ' ' + kind : '');
      oldest.kind = kind || '';
    }
    if (R.log.firstChild !== oldest.el) R.log.insertBefore(oldest.el, R.log.firstChild);
    return oldest;
  }

  function banner(text, sub, seconds) {
    if (!el || !text) return;
    const B = S.banner;
    B.txt = String(text); B.sub = sub ? String(sub) : '';
    B.t = 0; B.dur = seconds || 2.6;
    tx(R.banT, B.txt);
    tx(R.banS, B.sub);
  }

  /** Loud, short, all-caps messages become banners; everything else logs. */
  function toast(text, opts) {
    if (text == null) return;
    const s = String(text);
    const o = opts || null;
    if (o && o.banner) { banner(o.text || s, o.sub, o.seconds); log(s, o.kind); return; }
    if (s === 'MUSOU') {
      banner('MUSOU', 'Unleashed', 1.9);
      S.flash = 1;
      log('Musou unleashed', 'warn');
      return;
    }
    const fell = s.match(/^(.+?) has fallen$/);
    if (fell) { banner(fell[1], 'Officer Defeated', 2.4); log(s, 'good'); return; }
    if (s.length <= 16 && s === s.toUpperCase() && /[A-Z]/.test(s)) { banner(s, (o && o.sub) || '', (o && o.seconds) || 2.2); log(s, o && o.kind); return; }
    log(s, (o && o.kind) || (/parries|musou|harder|cornered/i.test(s) ? 'warn' : ''));
  }

  function damage(x, y, z, amount, kind, meta) {
    if (!settings.damage) return;
    dmg.spawn(x, y, z, amount, kind || 'hit', meta);
  }

  /* ============================================================== results */

  function showResults(data) {
    const d = data || {};
    const p = W && W.player;
    const bases = W && W.bases && W.bases.list;
    let held = 0;
    if (bases) for (let i = 0; i < bases.length; i++) if ((bases[i].team | 0) === 0) held++;
    const kills = d.kills !== undefined ? d.kills : (p ? p.kills : 0);
    const chain = d.maxChain !== undefined ? d.maxChain : (p ? p.maxChain : 0);
    const secs = d.seconds !== undefined ? d.seconds : (W && W.mission ? W.mission.elapsed : 0);
    let rank = d.rank, note = d.note;
    if (!rank) {
      const score = kills * 1.0 + chain * 2.2 + held * 40;
      rank = score >= 900 ? 'S' : score >= 620 ? 'A' : score >= 380 ? 'B' : score >= 180 ? 'C' : 'D';
      note = { S: 'Peerless', A: 'Renowned', B: 'Seasoned', C: 'Blooded', D: 'Survived' }[rank];
    }
    menus.results({
      title: d.title || 'VICTORY',
      subtitle: d.subtitle || 'Battle Report',
      kills, maxChain: chain, bases: d.bases !== undefined ? d.bases : held,
      time: d.time || mmss(secs), rank, note: d.note || note,
    });
    return rank;
  }

  /* =============================================================== states */

  /** Screens that are NOT a battle in progress, and so must not wear its HUD. */
  const FRONT_STATES = { title: 1, warriors: 1, arena: 1, briefing: 1, results: 1 };

  function onState(s) {
    cls(el, 'ui-playing', s === 'playing');
    cls(el, 'ui-front', !!FRONT_STATES[s]);
    if (s !== 'playing' && touchReleaseAll) touchReleaseAll();
    const run = !(s === 'pause' || s === 'options');
    if (W) W.flags.paused = !run;
    if (!allowEngineControl || CFG.debug.headless || !engine) return;
    try { if (run) engine.start(W); else engine.stop(); } catch (e) { /* ignore */ }
  }
  function setState(s) {
    if (!menus) return s;
    if (s === 'play') s = 'playing';
    return menus.show(s);
  }

  /* ================================================================ frame */

  function frame(w, realDt) {
    const t = w.time.real;
    const p = w.player;
    const e = w.entities;

    /* ---------------------------------------------------- health + musou */
    if (p) {
      const hpT = p.maxHp > 0 ? clamp01(p.hp / p.maxHp) : 0;
      if (hpT < S.hpPrev - 0.0005) S.hpHold = 0.42;
      S.hpPrev = hpT;
      S.hp = damp(S.hp, hpT, 18, realDt);
      if (S.hpGhost > S.hp) {
        S.hpHold -= realDt;
        if (S.hpHold <= 0) S.hpGhost = damp(S.hpGhost, S.hp, 5.2, realDt);
      } else S.hpGhost = S.hp;
      sx(R.hpFill, S.hp);
      sx(R.hpGhost, S.hpGhost);
      const hpn = Math.ceil(p.hp);
      if (R.hpNum.__n !== hpn) { R.hpNum.__n = hpn; R.hpNum.textContent = hpn; }
      const hpm = p.maxHp | 0;
      if (R.hpMax.__n !== hpm) { R.hpMax.__n = hpm; R.hpMax.textContent = ' / ' + hpm; }

      const muT = p.maxMusou > 0 ? clamp01(p.musou / p.maxMusou) : 0;
      S.musou = damp(S.musou, muT, 14, realDt);
      sx(R.muFill, S.musou);
      const ready = !!p.musouReady || muT > 0.995;
      S.musouGlow = damp(S.musouGlow, ready ? 1 : 0, 8, realDt);
      op(R.muRdy, S.musouGlow * (0.30 + 0.42 * (0.5 + 0.5 * Math.sin(t * 7.4))));
      cls(R.muLab, 'on', ready);

      /* ------------------------------------------------------- bravery */
      /* Whole points, so this writes only when a point changes hands — the
       * pips are rebuilt when the CAP changes (rank), and restyled when the
       * count does. A still frame writes nothing, same as every other row. */
      const cap = p.maxBravery | 0;
      if (cap !== S.brvCap) {
        S.brvCap = cap; S.brvN = -1;
        R.brvPips.textContent = '';
        for (let i = 0; i < cap; i++) R.brvPips.appendChild(document.createElement('u'));
      }
      const bn = Math.floor((p.bravery || 0) + 1e-6);
      if (bn !== S.brvN) {
        S.brvN = bn;
        const art = BRAVERY.art;
        const kids = R.brvPips.childNodes;
        for (let i = 0; i < kids.length; i++) {
          const lit = i < bn;
          // "art" marks the pips that are paid for once the expensive tier is
          // affordable, so the player sees the option open rather than counting
          cls(kids[i], 'on', lit && bn < art);
          cls(kids[i], 'art', lit && bn >= art);
        }
        cls(R.brvLab, 'on', bn >= art);
      }

      /* ------------------------------------------------------ KO counter */
      if (p.kills !== S.kills) {
        const first = S.kills < 0;
        S.kills = p.kills;
        drawKO(S.kills);
        if (!first) {
          S.koPunch = 0;
          if (S.kills > 0 && S.kills % 100 === 0) {
            banner(S.kills + ' K.O.', 'The field is thinning', 2.0);
            sound('ko_chime');
          }
        }
      }
      if (S.koPunch < 0.55) {
        S.koPunch += realDt;
        const k = S.koPunch;
        const s = 1 + 0.48 * Math.exp(-k * 9.5) * Math.cos(k * 21);
        R.koNum.style.transform = 'scale(' + s.toFixed(3) + ')';
        if (S.koPunch >= 0.55) R.koNum.style.transform = 'scale(1)';
      }

      /* ----------------------------------------------------- hit chain */
      const ch = p.chain | 0;
      if (ch !== S.chain) {
        if (ch > S.chain && ch >= 2) S.chainPunch = 0;
        S.chain = ch;
        if (ch >= 2) drawChain(ch);
      }
      const want = ch >= 2 ? 1 : 0;
      S.chainShow = want ? Math.min(1, S.chainShow + realDt * 9) : Math.max(0, S.chainShow - realDt * 2.6);
      op(R.chain, S.chainShow);
      if (S.chainPunch < 0.5) {
        S.chainPunch += realDt;
        const k = S.chainPunch;
        const s = 1 + 0.55 * Math.exp(-k * 11) * Math.cos(k * 24);
        R.chainNum.style.transform = 'scale(' + s.toFixed(3) + ')';
      }
      if (S.chainShow > 0.01) {
        const best = p.maxChain | 0;
        tx(R.chainBest, best > ch ? 'BEST ' + best : '');
      }
    }

    /* ---------------------------------------------------- officer plaque */
    const eng = w.ai && w.ai.engaged;
    const live = eng && eng.alive && eng.maxHp > 0;
    S.off = live ? Math.min(1, S.off + realDt * 5.5) : Math.max(0, S.off - realDt * 3.2);
    op(R.off, S.off);
    if (live) {
      if (eng.id !== S.offId) {
        S.offId = eng.id;
        S.offHp = S.offGhost = clamp01(eng.hp / eng.maxHp);
        tx(R.offName, eng.name || 'Officer');
        tx(R.offTitle, eng.title || '');
      }
      const f = clamp01(eng.hp / eng.maxHp);
      if (f < S.offHp - 0.0005) S.offHold = 0.36;
      S.offHp = damp(S.offHp, f, 20, realDt);
      if (S.offGhost > S.offHp) {
        S.offHold -= realDt;
        if (S.offHold <= 0) S.offGhost = damp(S.offGhost, S.offHp, 4.6, realDt);
      } else S.offGhost = S.offHp;
      sx(R.offFill, S.offHp);
      sx(R.offGhost, S.offGhost);
      const lit = 4 - clamp(eng.phase | 0 || 1, 1, 3);
      for (let i = 0; i < R.pips.length; i++) cls(R.pips[i], 'on', i < lit);
      S.offVuln = damp(S.offVuln, eng.vulnerable ? 1 : 0, 12, realDt);
      op(R.offVuln, S.offVuln * (0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 13))));
      const d = Math.round(eng.dist);
      if (R.offDist.__d !== d) { R.offDist.__d = d; R.offDist.textContent = d < 90 ? d + ' m' : ''; }
    } else S.offId = -1;

    /* --------------------------------------------------------- mission log */
    for (let i = 0; i < logs.length; i++) {
      const L = logs[i];
      if (L.t > L.life + 1) continue;
      L.t += realDt;
      const inK = clamp01(L.t / 0.24);
      const outK = L.life > 0 ? clamp01((L.t - L.life) / 0.9) : 1;
      const a = inK * (1 - outK);
      op(L.el, a);
      const off = (1 - inK) * -16 * u;
      if (L.el.__ox === undefined || Math.abs(L.el.__ox - off) > 0.3) {
        L.el.__ox = off;
        L.el.style.transform = 'translateX(' + off.toFixed(1) + 'px)';
      }
    }

    /* -------------------------------------------------------------- banner */
    const B = S.banner;
    if (B.t < B.dur + 0.9) {
      B.t += realDt;
      const inK = clamp01(B.t / 0.30);
      const outK = clamp01((B.t - B.dur) / 0.55);
      const a = inK * (1 - outK);
      op(R.ban, a);
      const scale = 1.05 - 0.05 * inK + outK * 0.03;
      const trans = 'scale(' + scale.toFixed(3) + ')';
      if (R.ban.__tr !== trans) { R.ban.__tr = trans; R.ban.style.transform = trans; }
      const rw = (inK * inK) * 430 * u;
      for (let i = 0; i < R.banR.length; i++) px(R.banR[i], 'width', rw);
    }

    /* ------------------------------------------------------------ vignette */
    S.hurt = Math.max(0, S.hurt - realDt * 2.4);
    S.flash = Math.max(0, S.flash - realDt * 3.4);
    const hpF = p ? clamp01(p.hp / p.maxHp) : 1;
    S.low = damp(S.low, hpF < 0.32 ? (0.32 - hpF) / 0.32 : 0, 5, realDt);
    if (settings.vignette) {
      op(R.vigHurt, S.hurt * S.hurt * 0.9);
      op(R.vigLow, S.low * (0.34 + 0.30 * (0.5 + 0.5 * Math.sin(t * 4.6))));
      op(R.flash, S.flash * S.flash * 0.55);
      op(R.vig, 1);
    } else { op(R.vigHurt, 0); op(R.vigLow, 0); op(R.flash, 0); op(R.vig, 0); }

    /* -------------------------------------------------------------- clock */
    if (w.mission) {
      const c = mmss(w.mission.elapsed);
      if (c !== S.clock) { S.clock = c; R.clock.textContent = c; }
    }

    /* --------------------------------------------------------------- tide */
    const mo = w.ai && w.ai.morale;
    if (mo) {
      const a = mo[0] || 0.5, b = mo[1] || 0.5;
      S.tide = damp(S.tide, a / Math.max(0.001, a + b), 3, realDt);
      const tw = 196 * u - 16 * u;
      px(R.tideA, 'width', S.tide * tw);
      px(R.tideB, 'width', (1 - S.tide) * tw);
    }

    /* ------------------------------------------------------------ minimap */
    if (mm.built) {
      S.mapT += realDt;
      if (S.mapT > 1 / 32 || w.time.frame < 3) {
        S.mapT = 0;
        mm.draw(w);
        const g = R.map.getContext('2d');
        if (R.map.width !== mm.canvas.width) { R.map.width = mm.canvas.width; R.map.height = mm.canvas.height; }
        g.clearRect(0, 0, R.map.width, R.map.height);
        g.drawImage(mm.canvas, 0, 0);
      }
    }

    /* ------------------------------------------------- world-space numbers */
    if (fxCtx) {
      const cam = w.camera;
      cam.updateMatrixWorld();
      if (rtDirty) {
        fxCtx.clearRect(rt0x, rt0y, rt1x - rt0x, rt1y - rt0y);
        rtDirty = false;
      }
      dmg.draw(fxCtx, vw, vh, cam, realDt, THREE, _v3);
      drawReticle(w, t);
    }

    /* ------------------------------------------------------------- stats */
    if (CFG.debug.showStats) {
      S.statT += realDt;
      if (S.statT > 0.25) {
        S.statT = 0;
        const P = w.perf;
        R.stat.textContent = `${P.fps.toFixed(0)} fps  ${P.cpuMs.toFixed(1)} ms  ${P.drawCalls} dc  ${(P.triangles / 1000) | 0}k tri  ${e.stats.total} ents`;
      }
    } else if (R.stat.textContent) R.stat.textContent = '';
  }

  /** Lock-on reticle, drawn on the fx canvas with its own dirty rect. */
  function drawReticle(w, t) {
    const rig = w.camrig, ret = rig && rig.reticle;
    if (!ret || !ret.active || !ret.onScreen) return;
    const x = ret.x, y = ret.y, r = ret.radius * 0.6;
    const g = fxCtx;
    g.save();
    g.translate(x, y);
    g.rotate(t * 0.9);
    g.strokeStyle = 'rgba(255,226,166,.85)';
    g.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2;
      g.beginPath();
      g.arc(0, 0, r, a + 0.24, a + Math.PI / 2 - 0.24);
      g.stroke();
    }
    g.strokeStyle = 'rgba(255,120,80,.9)';
    g.lineWidth = 1.5;
    g.beginPath(); g.arc(0, 0, r * 0.42, 0, Math.PI * 2); g.stroke();
    g.restore();
    const pad = r + 6;
    rt0x = x - pad; rt0y = y - pad; rt1x = x + pad; rt1y = y + pad;
    rtDirty = true;
  }

  /* ================================================================ system */

  return {
    name: 'ui', order: ORDER.UI,

    init(w) {
      W = w;
      engine = w.engine;
      load();
      build();

      menus = createMenus(el, {
        world: w, settings, sound, onState, save,
        setQuality, setVolume,
      });
      installTouch(w);

      // apply stored settings to the world
      if (settings.quality !== CFG.quality) setQuality(settings.quality);
      setVolume('master', settings.master);
      setVolume('music', settings.music);
      setVolume('sfx', settings.sfx);
      w.input?.setSensitivity?.(settings.sensitivity);

      const p = w.player;
      tx(R.pname, (p && p.name) || DEF_NAME);
      tx(R.ptitle, (p && p.title) || DEF_TITLE);

      try { mm.build(w); } catch (err) { console.warn('[ui] minimap bake failed', err); }
      const nm = w.bases && w.bases.list && w.bases.list.length ? ARENA_NAME : 'Field';
      const tag = el.querySelector('.mtag');
      if (tag) tx(tag, nm);

      /* --------------------------------------------------- world hooks -- */
      const bus = w.bus;
      // combat calls ui.damage() and then emits 'hit' with the full opts bag;
      // that lets us restyle the number we just spawned as a crit / player hit.
      bus.on('hit', (id, srcId, amount, opts) => {
        if (id === w.playerId) {
          dmg.upgrade('player');
          S.hurt = Math.min(1, S.hurt + 0.55 + clamp01(amount / 90) * 0.45);
        } else if (opts && (opts.crit || opts.guardBreak)) dmg.upgrade('crit');
      });
      bus.on('guarded', (id) => {
        const e = w.entities;
        if (id < 0 || !e.alive[id]) return;
        dmg.spawn(e.x[id], e.y[id] + 1.5, e.z[id], 'GUARD', 'block');
      });
      bus.on('kill', (id, srcId) => {
        if (id === w.playerId) { banner('Defeated', 'You have fallen', 3.2); S.hurt = 1; }
        // no per-kill chime: the player fells hundreds of men a minute and
        // audio already scores the impact. Milestones only (see the KO block).
      });
      bus.on('base:captured', (b) => {
        if (!b) return;
        const ours = (b.team | 0) === 0;
        banner(b.name || 'Base', ours ? 'Captured' : 'Lost', 2.4);
        log((b.name || 'A base') + (ours ? ' has been taken' : ' has fallen to ' + FOES.name), ours ? 'good' : 'bad');
      });
      bus.on('player:musou', (phase) => { if (phase === 'start') S.flash = 1; });

      /* --------------------------------------------------------- publish */
      const api = {
        version: 2,
        root: el, el,
        toast, damage, banner, log, setState, showResults,
        minimap: mm,
        damageStats: dmg.stats,
        settings, setQuality, setVolume,
        notify: log,
        /**
         * The front end's committed warrior. Repaints the portrait plate and
         * renames the health bar; called by `roster.previewHero()` as the
         * highlight moves, so the plate is already right the moment the
         * battle starts.
         */
        setWarrior(h) {
          if (!h) return null;
          heroArt = h.art || {};
          tx(R.pname, h.name || DEF_NAME);
          tx(R.ptitle, h.title || DEF_TITLE);
          const ps = 78 * u;
          try { portrait(fit(R.portrait, ps, ps, Math.min(dpr, 2)), ps, ps, heroArt); }
          catch (err) { /* called before the first layout() */ }
          return h;
        },
        /**
         * The muster chain, driven. Capture scenarios and the browser console
         * both need to walk title -> warrior -> field -> orders without a
         * keyboard, and a screen nobody can drive headlessly is a screen no
         * critic ever looks at.
         */
        front: {
          get state() { return menus ? menus.state : 'playing'; },
          get chain() { return menus ? menus.CHAIN.slice() : []; },
          get choice() { return menus ? { ...menus.select.choice } : null; },
          /** The roster on offer, for a driver that must not hard-code it. */
          get heroes() { return menus ? menus.select.heroes.slice() : []; },
          get stages() { return menus ? menus.select.stages.slice() : []; },
          hero(i) { return menus ? menus.select.setHero(i) : -1; },
          arena(i) { return menus ? menus.select.setArena(i) : -1; },
          next() { return menus ? menus.forward(menus.state) : null; },
          back() { return menus ? menus.backward(menus.state) : null; },
          open() { return menus ? menus.openSelect() : null; },
          muster() { return menus ? menus.muster() : null; },
        },
        /** The front end's committed battlefield: renames the minimap plate. */
        setArena(a) {
          if (!a) return null;
          const tag = el.querySelector('.mtag');
          if (tag) tx(tag, a.name);
          return a;
        },
        hide() { hidden = true; cls(el, 'off', true); },
        show() { hidden = false; cls(el, 'off', false); },
        get hidden() { return hidden; },
        get state() { return menus ? menus.state : 'playing'; },
        clear() { dmg.clear(); for (const L of logs) { L.t = 99; op(L.el, 0); } S.banner.t = 99; op(R.ban, 0); },
        /** capture/debug helper: force every element to a readable value */
        demo(on) { hidden = false; cls(el, 'off', false); return on; },
      };
      w.ui = api;

      layout(engine.size.w, engine.size.h, engine.size.dpr);

      if (!CFG.debug.headless) {
        // Back from a change of field (select.js switchField): straight to the
        // battlefield step, same warrior in hand — not the title screen again.
        const q = new URLSearchParams(location.search);
        if (q.get('front') === 'field') {
          menus.openSelect();
          const hi = menus.select.heroes.findIndex((h) => h.id === q.get('hero'));
          if (hi >= 0) menus.select.setHero(hi);
          menus.forward(menus.state);
        } else menus.show('title');
      } else menus.show('playing');

      log(ALLIES.name + ' vanguard has entered the field', 'good');
      // Written at boot, before the player has chosen anything: name no stage.
      log('Awaiting the order to march', '');
    },

    update(w) {
      allowEngineControl = true;
      // Input owns normalization; updating this persistent scalar here makes
      // menu changes effective on the following render frame without mutating
      // a delta after the camera has already consumed it.
      w.input?.setSensitivity?.(settings.sensitivity);
    },

    lateUpdate(w, dt, alpha, realDt) {
      if (hidden || !el) return;
      try { frame(w, realDt > 0 ? Math.min(realDt, 0.1) : 1 / 60); } catch (err) {
        // A HUD throw must never take the boot or a capture with it.
        if (!frame.__warned) { frame.__warned = 1; console.warn('[ui] frame error', err); }
      }
    },

    resize(w, h, d) { layout(w, h, d); },

    dispose() {
      if (touchCleanup) touchCleanup();
      if (menus) menus.dispose();
      el && el.remove();
    },
  };
}
